import type { DynamicSocketDefinition } from "./socketDefinition";

/**
 * Marker suffix carried by every dynamic socket id generated under
 * `__socketIdVersion: 2`.
 *
 * Socket ids do quadruple duty: socket identity on a node, the key the engine
 * reads the upstream payload with (`sourceOutputData[edge.sourceSocketId]` in
 * `graphTraversal`), a shared namespace with orchestration ids like
 * `__flow_control_in__`, and the join key inside stored socket expressions.
 * Dynamic ids come from user data — CSV column names, switch case labels,
 * sandbox variable keys — so without a marker a user-named socket can land on
 * top of one of the step's own fixed sockets, and the collision is silent.
 *
 * `::` is the discriminator: a fixed socket id must never contain it (asserted
 * by `fixedSocketIds.test.ts`), so the two namespaces cannot intersect. That is
 * one rule in place of the per-family prefixes (`col_`, `field:`) and
 * reserved-id blocklists that grew up around individual steps.
 */
export const DYNAMIC_SOCKET_ID_MARKER = "::dynamic";

/**
 * `WorkflowEdge.sourceSocketId` / `targetSocketId` are `STRING(100)`, so an id
 * longer than this cannot be persisted on an edge at all.
 */
export const SOCKET_ID_MAX_LENGTH = 100;

/** True when `id` was generated under the marker scheme. */
export function isDynamicSocketId(id: string): boolean {
  return id.endsWith(DYNAMIC_SOCKET_ID_MARKER);
}

/**
 * Build the marked socket id for a user-supplied `name`.
 *
 * The name is truncated so the marker always survives — the marker is what
 * makes the id safe, and a truncation that dropped it would produce an id that
 * looks fixed and can collide.
 */
export function dynamicSocketId(name: string): string {
  const budget = SOCKET_ID_MAX_LENGTH - DYNAMIC_SOCKET_ID_MARKER.length;
  if (name.length <= budget) {
    return `${name}${DYNAMIC_SOCKET_ID_MARKER}`;
  }

  // Plain truncation would map every name sharing this prefix onto one id, and
  // `buildDynamicSockets` would then refuse all but the first — silently
  // dropping a configured socket. The digest is of the WHOLE name, so two long
  // names differing only past the cut still get distinct ids. Only over-budget
  // names carry it, so ordinary ids stay readable.
  const digest = nameDigest(name);
  const head = sliceCodeUnitsWholeCharacters(name, budget - digest.length - 1);
  return `${head}_${digest}${DYNAMIC_SOCKET_ID_MARKER}`;
}

/**
 * Take at most `budget` UTF-16 code units from `name` without splitting a
 * character.
 *
 * A plain `.slice()` counts code units, so a cut landing inside an astral
 * character — an emoji in a switch case label, say, now that version 2 accepts
 * free text — leaves a lone surrogate. That is not encodable as UTF-8, so it
 * cannot survive a round trip through the database: the id persisted on the
 * edge would differ from the one the handler re-derives, and the branch would
 * stop routing with nothing raised.
 *
 * The budget stays in code units because the limit it serves is the
 * `STRING(100)` edge column.
 */
function sliceCodeUnitsWholeCharacters(name: string, budget: number): string {
  let out = "";
  // Iterating a string yields whole code points, never half a surrogate pair.
  for (const character of name) {
    if (out.length + character.length > budget) break;
    out += character;
  }
  return out;
}

/**
 * Short deterministic digest of a socket name (FNV-1a, base36).
 *
 * Hand-rolled rather than `crypto`: this runs in the editor as well as the
 * backend, and both must derive the same id from the same name. The output is
 * PERSISTED on edges and inside `(output ...)` expressions, so this function's
 * result for a given input can never change once shipped.
 */
function nameDigest(name: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < name.length; index += 1) {
    hash ^= name.charCodeAt(index);
    // >>> 0 keeps it an unsigned 32-bit value; Math.imul avoids the precision
    // loss a plain `*` would hit on the 16777619 multiply.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(7, "0");
}

/**
 * Socket-id scheme in force for a step, read from its persisted config.
 *
 * Version 1 (anything without the key) derives ids as the bare user name, which
 * is what every workflow saved before the marker shipped has on its edges.
 * Version 2 applies the marker. The version is stamped on a step when it is
 * created and never changed afterwards, because changing it would rewrite the
 * step's socket ids while its edges, its `(output ...)` expressions and its
 * `__static_inputs` keys still referenced the old ones.
 */
export const SOCKET_ID_VERSION_CONFIG_KEY = "__socketIdVersion";

/** The scheme new steps are stamped with. */
export const CURRENT_SOCKET_ID_VERSION = 2;

export function socketIdVersion(config: Record<string, unknown>): number {
  const raw = config[SOCKET_ID_VERSION_CONFIG_KEY];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 1;
}

/**
 * Step types whose handler reads and writes its payload by socket id, and which
 * may therefore be stamped with version 2 when a new step is created.
 *
 * A step type belongs here only once its handler is converted. Marking a
 * socket `sku::dynamic` while the handler still writes `output[column.name]`
 * would leave the engine looking up `sku::dynamic` in a payload keyed `sku` —
 * it would read `undefined` and the branch would die silently. So enabling a
 * step type here and converting its handler are one change, never two.
 *
 * Only new steps consult this. A step already stamped keeps its version
 * forever: re-deriving its ids would rewrite them while its edges, its
 * `(output ...)` expressions and its `__static_inputs` keys still referenced
 * the old ones.
 */
export const SOCKET_ID_V2_STEP_TYPES: ReadonlySet<string> = new Set<string>([]);

export function supportsSocketIdV2(stepType: string): boolean {
  return SOCKET_ID_V2_STEP_TYPES.has(stepType);
}

/**
 * The socket-id version a generator must actually use for `stepType`.
 *
 * `socketIdVersion` reads the stamp off config, and config arrives from the
 * client on every save and from imported templates — so the stamp alone is a
 * claim, not authorization. A step of a type whose handler still reads and
 * writes bare configured names would, if that claim were honored, get marked
 * ids on the canvas and nowhere to deliver them: inputs arrive undefined,
 * outputs land on keys no edge references, and nothing raises.
 *
 * The opt-in set is the authority. A stamp on an unconverted type is ignored
 * rather than rejected, because refusing it would break the save of a workflow
 * whose config merely carries a stale key.
 */
export function effectiveSocketIdVersion(
  stepType: string,
  config: Record<string, unknown>,
): number {
  return supportsSocketIdV2(stepType) ? socketIdVersion(config) : 1;
}

/**
 * Force an EXISTING step's socket-id stamp back to what was persisted for it.
 *
 * The stamp is written once, when the step is created, and must never move
 * afterwards: it decides whether the step's sockets are `sku` or
 * `sku::dynamic`, while its edges, its stored `(output ...)` expressions and
 * its `__static_inputs` keys still name the ids derived under the old value.
 * Nothing about a draft save enforces that on its own — `updateWorkflowDefinition`
 * persists client-sent config wholesale, so a crafted or stale payload could
 * add, change or drop the key and silently re-derive every dynamic socket id on
 * that step.
 *
 * Both directions matter. Dropping the stamp from a version 2 step reverts its
 * ids to bare; adding one to a legacy step marks ids that every existing edge
 * still references bare. Either way the edges survive the save pointing at ids
 * no socket has any more.
 *
 * Call this ONLY for steps that already exist. A newly created step has no
 * persisted value to preserve, and its stamp comes from
 * `newStepSocketIdVersionConfig`, which is gated on the opt-in set.
 */
export function preserveSocketIdVersion(
  config: Record<string, unknown>,
  persistedVersion: unknown,
): Record<string, unknown> {
  const { [SOCKET_ID_VERSION_CONFIG_KEY]: _incoming, ...rest } = config;
  return typeof persistedVersion === "number" &&
    Number.isFinite(persistedVersion)
    ? { ...rest, [SOCKET_ID_VERSION_CONFIG_KEY]: persistedVersion }
    : rest;
}

/**
 * The `__socketIdVersion` entry to merge into a newly created step's config, or
 * nothing when the step type is not converted yet. Spread into the config at
 * every step-creation site so the stamp cannot be applied inconsistently.
 */
export function newStepSocketIdVersionConfig(
  stepType: string,
): Record<string, number> {
  return supportsSocketIdV2(stepType)
    ? { [SOCKET_ID_VERSION_CONFIG_KEY]: CURRENT_SOCKET_ID_VERSION }
    : {};
}

/** A user-named entry that becomes one dynamic socket. */
export interface DynamicSocketEntry {
  /** The user-supplied name this socket's id derives from. */
  name: string;
  /** Everything about the socket except its id. */
  socket: Omit<DynamicSocketDefinition, "id">;
}

/** A dynamic socket that could not be emitted, and why. */
export interface RefusedDynamicSocket {
  name: string;
  reason: "reserved" | "duplicate";
}

export interface BuildDynamicSocketsResult {
  sockets: DynamicSocketDefinition[];
  refused: RefusedDynamicSocket[];
}

/**
 * Build one socket per user-named entry, guaranteeing no two of them share an
 * id and none collides with a `reserved` id.
 *
 * Returns only the dynamic sockets; the caller composes them with its fixed
 * ones. That is deliberate — each family orders the two groups differently
 * (`switch` emits cases then `default`, `identify_customer` emits `top_pick`
 * then routes then `others`, `use_data_mapping` emits its fixed pair first),
 * and the emitted order is persisted on the step, so a helper that imposed one
 * order would rewrite existing nodes' socket arrays.
 *
 * This generalizes the `seenIds` guard PR #5570 added to `csv_transform_v4`,
 * which was the only family that had one. Two distinct collisions are possible
 * and both are handled:
 *
 *  - **fixed vs dynamic** — a column named `rows`, a case labelled `default`.
 *    Under version 2 the marker makes this impossible, so nothing is refused.
 *    Under version 1 the entry is refused, matching the old blocklists.
 *  - **dynamic vs dynamic** — two cases sharing a label, two dynamic inputs
 *    sharing a name. The marker cannot help here (both derive the same id), so
 *    the later entry is refused in both versions.
 *
 * Refusals are returned rather than thrown so each caller decides: a config
 * schema can surface them as validation errors, while a socket generator
 * running on already-persisted config must not explode.
 */
export function buildDynamicSockets({
  reserved = [],
  entries,
  version,
}: {
  reserved?: readonly string[];
  entries: DynamicSocketEntry[];
  version: number;
}): BuildDynamicSocketsResult {
  const reservedIds = new Set(reserved);
  const seenIds = new Set(reservedIds);
  const sockets: DynamicSocketDefinition[] = [];
  const refused: RefusedDynamicSocket[] = [];

  for (const entry of entries) {
    const id =
      version >= CURRENT_SOCKET_ID_VERSION
        ? dynamicSocketId(entry.name)
        : entry.name;

    if (seenIds.has(id)) {
      // Under version 2 a marked id can only collide with another marked id,
      // so a hit here is always a genuine duplicate name.
      refused.push({
        name: entry.name,
        reason: reservedIds.has(id) ? "reserved" : "duplicate",
      });
      continue;
    }

    seenIds.add(id);
    sockets.push({ id, ...entry.socket });
  }

  return { sockets, refused };
}
